/**
 * Support API HTTP Client
 * Routes requests to the support-api backend (separate microservice)
 */

import { parseResponse } from "./response-parser";
import { FetchError } from "./errors";
import { DEFAULT_TIMEOUT, HTTP_METHODS } from "./constants";
import type { FetchOptions } from "./types";

const SUPPORT_BASE_URL =
  process.env.NEXT_PUBLIC_SUPPORT_API_URL ||
  process.env.SUPPORT_API_URL ||
  "http://localhost:8006";

async function supportFetch<T>(
  path: string,
  options?: FetchOptions,
): Promise<T> {
  const { params, timeout = DEFAULT_TIMEOUT, ...fetchOptions } = options || {};

  const cleanPath = path.replace(/^\//, "");
  const fullUrl = new URL(`${SUPPORT_BASE_URL}/api/v1/${cleanPath}`);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        fullUrl.searchParams.set(key, String(value));
      }
    });
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(fullUrl.toString(), {
      ...fetchOptions,
      signal: controller.signal,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...fetchOptions.headers,
      },
    });

    if (!response.ok) {
      let errorMessage = `HTTP error: ${response.statusText}`;
      try {
        const errorBody = await response.json();
        if (errorBody.detail) {
          errorMessage = errorBody.detail;
        }
      } catch {
        // Response body is not JSON
      }
      throw new FetchError(
        errorMessage,
        response.status,
        response.statusText,
        fullUrl.toString(),
        fetchOptions.method,
      );
    }

    return parseResponse<T>(response);
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      throw new FetchError(
        "Request timeout",
        408,
        "Request Timeout",
        fullUrl.toString(),
        fetchOptions.method,
      );
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

export const supportGet = <T>(
  url: string,
  options?: FetchOptions,
): Promise<T> => supportFetch<T>(url, { ...options, method: HTTP_METHODS.GET });

/** Pulls the download filename out of a `Content-Disposition` header, if present. */
function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  // Prefer the RFC 5987 `filename*=UTF-8''name` form; fall back to plain `filename="name"`.
  const encoded = header.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1].replace(/^"|"$/g, ""));
    } catch {
      // Malformed percent-encoding — fall through to the plain form.
    }
  }
  const plain = header.match(/filename="?([^";]+)"?/i);
  return plain?.[1] ?? null;
}

/**
 * GETs a binary attachment (not JSON) from support-api, keeping the same auth,
 * credentials and `FetchError` conventions as {@link supportGet}. The response body is
 * returned as a raw `Blob` alongside the server-provided filename so the caller can drive
 * the browser download — the endpoint needs the Authorization header, so it cannot be a
 * plain `<a href download>`.
 */
export const supportGetBlob = async (
  url: string,
  options?: FetchOptions,
): Promise<{ blob: Blob; filename: string | null }> => {
  const { params, timeout = DEFAULT_TIMEOUT, ...fetchOptions } = options || {};

  const cleanPath = url.replace(/^\//, "");
  const fullUrl = new URL(`${SUPPORT_BASE_URL}/api/v1/${cleanPath}`);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        fullUrl.searchParams.set(key, String(value));
      }
    });
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(fullUrl.toString(), {
      ...fetchOptions,
      method: HTTP_METHODS.GET,
      signal: controller.signal,
      credentials: "include",
      headers: { ...fetchOptions.headers },
    });

    if (!response.ok) {
      let errorMessage = `HTTP error: ${response.statusText}`;
      try {
        const errorBody = await response.json();
        if (errorBody.detail) errorMessage = errorBody.detail;
      } catch {
        // Response body is not JSON (or empty) — keep the status-text message.
      }
      throw new FetchError(
        errorMessage,
        response.status,
        response.statusText,
        fullUrl.toString(),
        HTTP_METHODS.GET,
      );
    }

    return {
      blob: await response.blob(),
      filename: parseContentDispositionFilename(
        response.headers.get("content-disposition"),
      ),
    };
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      throw new FetchError(
        "Request timeout",
        408,
        "Request Timeout",
        fullUrl.toString(),
        HTTP_METHODS.GET,
      );
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

export const supportPost = <TResponse>(
  url: string,
  body: unknown,
  options?: FetchOptions,
): Promise<TResponse> =>
  supportFetch<TResponse>(url, {
    ...options,
    method: HTTP_METHODS.POST,
    body: JSON.stringify(body),
  });

export const supportPut = <TResponse>(
  url: string,
  body: unknown,
  options?: FetchOptions,
): Promise<TResponse> =>
  supportFetch<TResponse>(url, {
    ...options,
    method: HTTP_METHODS.PUT,
    body: JSON.stringify(body),
  });

export const supportPatch = <TResponse>(
  url: string,
  body: unknown,
  options?: FetchOptions,
): Promise<TResponse> =>
  supportFetch<TResponse>(url, {
    ...options,
    method: HTTP_METHODS.PATCH,
    body: JSON.stringify(body),
  });
